<template>
  <div class="widget w-holdings-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      csvExport
      printView
      optimizeColumns
      fieldsConfigManager="advanced"
      hideLinkModeReceive
      showTDFields
      :wid="wid"
      @csv-export="onCsvExport"
      @optimize-columns="autoSizeColumns"
    >
      <template #right>
        <c-refresh-button @refresh="refresh" />
        <div class="update-time-stamp">
          <span> {{ lastUpdatedTimeStamp }} </span>
        </div>
      </template>
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <div v-if="connectionBroken" class="no-data-splash">
      <h3>{{ i18n('connectionBrokenHoldings') }}</h3>
    </div>

    <div v-else class="widget-body">
      <c-widget-control-bar class="align-items-start">
        <div class="mw-100">
          <c-widget-account-select
            @accountChange="onAccountChange"
            :selectedAccountId="selectedAccountId ? selectedAccountId : defaultAccountStringID"
            :label-cols="null"
            :label="$t(`${i18nNamespace}.accountHeader.account`)"
            eligible
          />
          <div class="cash-buyingpower">
            <div class="form-group cb-bp">
              <label for="cash" class="cb-bp-label">{{ $t(`${i18nNamespace}.cash`) }}</label>
              <span>{{ $f(cash, 'float.priceTD') }}</span>
            </div>
            <div v-if="isBuyingPower" class="form-group cb-bp">
              <label for="bp" class="cb-bp-label">{{ $t(`${i18nNamespace}.buyingPower`) }}</label>
              <span>{{ $f(buyingPower, 'float.priceTD') }}</span>
            </div>
          </div>
        </div>
        <c-widget-input-search
          :label="$t('white-label.globalLabels.symbol')"
          :value="symbolSearch"
          @input="handleSymbolChange"
        />
        <c-widget-input-switch
          :label="$t(`${i18nNamespace}.accountHeader.groupBySector`)"
          :value="groupSector"
          :options="[
            { value: true, text: $t('white-label.globalLabels.yes') },
            { value: false, text: $t('white-label.globalLabels.no') }
          ]"
          @input="groupBySector"
        />
        <c-widget-input-switch
          :label="$t(`${i18nNamespace}.accountHeader.groupByUnderlying`)"
          :value="groupUnderlying"
          :options="[
            { value: true, text: $t('white-label.globalLabels.yes') },
            { value: false, text: $t('white-label.globalLabels.no') }
          ]"
          @input="groupByUnderlying"
        />
        <v-form-group :label="$t(`${i18nNamespace}.accountHeader.template`)">
          <v-form-select
            :options="fieldsConfigNameOptions"
            v-model="controlFieldsConfigName"
            :aria-label="
              $t(`${i18nNamespace}.accountHeader.template`) + ' ' + $t('white-label.globalLabels.screenReaderTemplate')
            "
          />
        </v-form-group>
      </c-widget-control-bar>

      <c-widget-control-bar class="p-0">
        <b-table-simple responsive borderless class="bg-transparent m-0" :aria-label="i18n('profitLoss.profitAndLoss')">
          <b-tbody>
            <!-- Collapsed -->
            <b-tr v-if="!plExpanded">
              <b-td class="pr-2">
                <v-button
                  @click="togglePL"
                  :aria-label="i18n('profitLoss.profitAndLossExpand')"
                  variant="link"
                  class="mt3"
                  ref="buttonPlus"
                  :aria-expanded="plExpanded ? 'true' : 'false'"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                </v-button>
              </b-td>
              <b-td class="pr-4">{{ i18n('profitLoss.profitAndLoss') }}</b-td>
              <b-td class="pr-4">
                <span class="label pr-3">{{ i18n('profitLoss.totalMarketValue') }}</span>
                <span>{{ totalMarketValue }}</span>
              </b-td>
              <b-td>
                <span class="label pr-3">{{ i18n('profitLoss.unrealizedPL') }}</span>
                <span :class="[getTextColor(totalUnrealizedPL)]">
                  {{ totalUnrealizedPL }} {{ totalUnrealizedPLPercent }}
                </span>
              </b-td>
              <b-td style="width: 100%"></b-td>
            </b-tr>

            <!-- Expanded -->
            <b-tr v-else>
              <b-td class="p-0">
                <b-tr>
                  <b-td>
                    <v-button
                      @click="togglePL"
                      :aria-label="i18n('profitLoss.profitAndLossExpand')"
                      variant="link"
                      class="mt3"
                      ref="buttonMinus"
                      :aria-expanded="plExpanded ? 'true' : 'false'"
                    >
                      <font-awesome-icon :icon="['fas', 'minus']" />
                    </v-button>
                  </b-td>
                  <b-td class="pr-5">
                    <div key="title">{{ i18n('profitLoss.profitAndLoss') }}</div>
                    <v-button
                      :variant="showCustomSelection ? 'primary' : 'secondary'"
                      @click="showCustomSelection = !showCustomSelection"
                      :pressed.sync="customSelectionToggle"
                    >
                      <span class="sr-only">{{ i18n('profitLoss.profitAndLoss') }}</span>
                      {{ i18n('profitLoss.customSelection') }}
                    </v-button>
                  </b-td>
                  <b-td class="pt-2 pr-5">
                    <c-widget-input-switch :value="currency" @input="onCurrencyChanged">
                      <v-form-radio value="CAD">
                        <v-flag countryCode="CA">{{ i18n('cad') }} {{ currencyConversionLabel }}</v-flag>
                      </v-form-radio>
                      <v-form-radio value="USD">
                        <v-flag countryCode="US">{{ i18n('usd') }} {{ currencyConversionLabel }}</v-flag>
                      </v-form-radio>
                    </c-widget-input-switch>
                    <div class="p-2">{{ currencyConversionLabel }}</div>
                  </b-td>
                </b-tr>
              </b-td>
              <b-td class="pr-5">
                <c-profit-loss-table :items="marketTableItems['marketValues']"></c-profit-loss-table>
              </b-td>
              <b-td>
                <c-profit-loss-table :items="marketTableItems['plValues']"></c-profit-loss-table>
              </b-td>
              <b-td style="width: 100%"></b-td>
            </b-tr>

            <!-- Custom Selection -->
            <b-tr v-if="plExpanded && showCustomSelection" class="custom-selection">
              <b-td></b-td>
              <b-td class="pr-5">
                <c-profit-loss-table :items="selectedMarketTableItems['marketValues']"></c-profit-loss-table>
              </b-td>
              <b-td>
                <c-profit-loss-table :items="selectedMarketTableItems['plValues']"></c-profit-loss-table>
              </b-td>
              <b-td style="width: 100%"></b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </c-widget-control-bar>
      <div class="widget-body">
        <c-loader v-if="isPositionsLoading" />
        <ag-grid-vue
          v-show="!isPositionsLoading"
          animateRows
          :asyncTransactionWaitMillis="gridUpdateInterval"
          suppressContextMenu
          :autoGroupColumnDef="autoGroupColumnDef"
          class="c-data-grid ag-theme-balham-dark"
          :columnDefs="columnDefs"
          :components="renderers"
          :context="this"
          :defaultColDef="defaultColDef"
          ensureDomOrder
          :gridOptions="gridOptions"
          groupDefaultExpanded="-1"
          :noRowsOverlayComponent="noRowsOverlayRenderer"
          :rowData="positions"
          :rowHeight="agGridRowHeight"
          rowSelection="multiple"
          :suppressRowClickSelection="showCustomSelection"
          suppressFieldDotNotation
          suppressDragLeaveHidesColumns
          :suppressColumnVirtualisation="isAccessibilityEnabled"
          maintainColumnOrder="false"
          @column-moved="onColMoved"
          @column-resized="onColResized"
          @drag-stopped="onGridDragEnd"
          @grid-ready="onGridReady"
          @row-selected="updateLinkAndNodes"
          @row-clicked="updateLink"
          @sort-changed="onTableSort"
          @row-group-opened="onRowGroupOpened"
          @cell-focused="onCellFocusedWidget"
          @row-data-changed="applyRowSelection"
          @new-columns-loaded="ev => setColumnState(ev.columnApi)"
          @async-transactions-flushed="onAsyncTransactionsFlushed"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FidletVue from '@/plugins/Fidlet';
import CLoader from '@custom/CLoader';
import CRefreshButton from '@custom/CRefreshButton';
import CToggleButton from '@custom/CToggleButton';
import { change as ChangeRulesPack } from '@custom/dataGrid/classRules';
import {
  FlagRenderer,
  GenericTickerRenderer,
  NoRowsOverlayRenderer,
  RowButtonRenderer,
  TdTradingButtonsRenderer,
  TickFlashRenderer
} from '@custom/dataGrid/renderers';
import CWidgetAccountSelect from '@custom/widgetContainer/CWidgetAccountSelect';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetInputSearch from '@custom/widgetContainer/CWidgetInputSearch';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';
import agGridCommons from '@mixins/agGridCommons.js';
import positions from '@mixins/positions.js';
import styleVars from '@mm5-components/scss/variables.scss';
import { AgGridVue } from 'ag-grid-vue';
import moment from 'moment';
import { mapGetters } from 'vuex';
import CProfitLossTable from './CProfitLossTable';

import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
fontAwesomeLibrary.add(faPlus, faMinus);

export default {
  name: 'w-holdings-widget',
  mixins: [agGridCommons, positions],
  components: {
    CRefreshButton,
    CWidgetHeader,
    CWidgetControlBar,
    CWidgetInputSearch,
    CWidgetInputSwitch,
    CToggleButton,
    CWidgetAccountSelect,
    AgGridVue,
    FlagRenderer,
    TickFlashRenderer,
    TdTradingButtonsRenderer,
    RowButtonRenderer,
    NoRowsOverlayRenderer,
    CLoader,
    FidletVue,
    GenericTickerRenderer,
    CProfitLossTable
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      cash: 0,
      buyingPower: 0,
      customSelectionToggle: false,
      currencyOptions: [
        { value: 'CAD', text: 'CA', variant: 'primary' },
        { value: 'USD', text: 'US', variant: 'primary' }
      ],
      i18nNamespace: 'white-label.widgets.holdings',
      plPanelVisible: false,
      plExpanded: true,
      gridApi: null,
      noRowsOverlayRenderer: 'NoRowsOverlayRenderer',
      gridOptions: {
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: this.applySymbolFilterForRow,
        getRowNodeId: data => {
          return data.ric || data.securityName;
        },
        suppressAggFuncInHeader: true,
        groupSelectsChildren: true,
        groupRemoveSingleChildren: false,
        groupRemoveLowestSingleChildren: false,
        icons: {
          groupExpanded: '<i class="fa fa-minus"/>',
          groupContracted: '<i class="fa fa-plus"/>'
        },
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      renderers: [FlagRenderer],
      groupSector: false,
      groupUnderlying: false,
      isOrderEntryAccount: false,
      tableFieldsPrepend: ['action']
    };
  },
  computed: {
    ...mapGetters({
      accounts: 'accounts/getAccountData',
      config: 'accounts/getConfigData',
      orderEntryAccounts: ['accounts/getOrderEntryAccountData'],
      defaultAccountId: 'accounts/getDefaultAccountId'
    }),
    connectionBroken() {
      return this._.isEmpty(this.config) || this._.isEmpty(this.accounts);
    },
    selectedAccountId: {
      get() {
        return this.matchedAccount ? this.matchedAccount.id : this.matchedAccount;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/UPDATE_ACCOUNTID`, value);
      }
    },
    matchedAccount() {
      let accounts = this.accounts;
      if (Array.isArray(accounts)) {
        return accounts.find(account => account.id === this.widgetStore.selectedAccountId);
      }
      return null;
    },
    defaultAccountStringID() {
      let accounts = this.accounts;
      if (Array.isArray(accounts)) {
        let defaultAccount = accounts.find(account => account.number === this.defaultAccountId);
        return defaultAccount ? defaultAccount.id : defaultAccount;
      }
      return null;
    },
    currency: {
      get() {
        return this.$store.state.widget[this.wid].currency;
      },
      set(value) {
        this.$store.commit(`widget/${this.wid}/SET_CURRENCY`, value);
      }
    },
    agGridRowHeight() {
      return parseInt(styleVars['rowHeight' + this.fontSize.toUpperCase()]);
    },
    columnDefs() {
      const cols = this.tableFids.reduce((acc, fid) => {
        let field = fid;
        if (fid.startsWith('td.')) {
          field = fid.substring(3);
        }
        const cfg = {
          field,
          headerName: this.i18n(fid + '.short', 'white-label.globalLabels.fids'),
          headerTooltip: this.i18n(fid + '.full', 'white-label.globalLabels.fids'),
          pinned: this.relatedFieldsConfig.pinnedFids.includes(fid),
          ...(this.fidColumnDefs[fid] || {}),
          ...(this.extendedColDefs[field] || {})
        };
        cfg.lockPosition = cfg.pinned;
        if (field !== 'action') cfg.pinned = cfg.pinned && this.pinColumnsAllowed;
        acc.push(cfg);
        return acc;
      }, []);
      return [...cols, ...this.groupColumns];
    },
    defaultColDef() {
      return Object.assign(this.defaultColumnDef, {
        cellClass: 'text-right',
        resizable: true,
        sortable: true,
        suppressMenu: true,
        aggFunc: () => ''
      });
    },
    autoGroupColumnDef() {
      return {
        ...this.tickerColDefForGroupedCol,
        headerName: this.i18n('td.ticker.short', 'white-label.globalLabels.fids'),
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRendererFramework: 'GenericTickerRenderer',
          innerRendererParams: params => {
            const groupSRText = this.i18n('groupPressEnter', this.i18nNamespace);
            if (params.data && params.data['q.TRD_STATUS']) {
              return {
                ticker: this.displayTicker(params.data),
                tradeHaltValue: params.data['q.TRD_STATUS'],
                priceChange: params.data['q._NETCHNG_1'],
                groupSRText
              };
            } else {
              return { ticker: params.valueFormatted, groupSRText };
            }
          }
        },
        suppressKeyboardEvent: params => {
          const event = params.event;
          let checkboxElement = null;

          if (!['Tab', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return false;

          const cellElement = event.srcElement.closest('.ag-cell');

          const groupControlElements = cellElement.querySelectorAll(
            'span.ag-group-expanded:not(.ag-hidden), span.ag-group-contracted:not(.ag-hidden)'
          );

          if (groupControlElements.length === 0) return false;

          const isCheckboxFocused = event.srcElement.tagName === 'INPUT';
          if (isCheckboxFocused) checkboxElement = event.srcElement;
          else checkboxElement = cellElement.querySelector('div.ag-selection-checkbox:not(.ag-hidden) input');

          const isBackward = (event.key === 'Tab' && event.shiftKey) || event.key === 'ArrowLeft';

          // selecting Expand/Collapse
          if (isCheckboxFocused && !isBackward) {
            groupControlElements.forEach(element => {
              element.classList.add('active');
            });
            cellElement.focus();

            event.preventDefault();
            return true;
          } else
            groupControlElements.forEach(element => {
              element.classList.remove('active');
            });

          // selecting checkbox
          if (checkboxElement && !isCheckboxFocused && isBackward) {
            checkboxElement.focus();
            event.preventDefault();
            return true;
          }

          return false;
        },
        comparator: (a, b, nodeA, nodeB) => {
          if (!nodeA.group || !nodeB.group) return 0;
          return a.localeCompare(b);
        },
        type: ['actionable']
      };
    },
    extendedColDefs() {
      return {
        action: {
          cellRenderer: 'RowButtonRenderer',
          cellRendererParams: params => {
            let hide = true;
            let isFractional;
            if (params.data && !params.node.group) {
              const isOpt = params.data.symbol && params.data.symbol.type === 'OPTION';
              hide = this._.isEmpty(params.data.ric) || (isOpt ? this._.isEmpty(params.data.underlyingRic) : false);
              isFractional = !Number.isInteger(params.data.quantity);
            }
            return { accountId: this.selectedAccountId, hide, isFractional };
          },
          type: ['actionable']
        },
        ticker: {
          ...this.tickerColDef,
          hide: this.groupSector || this.groupUnderlying,
          type: ['actionable']
        },
        country: {
          valueGetter: params => {
            const country = params.data && params.data.symbol ? params.data.symbol.market : undefined;
            return country === 'CA' || country === 'US' ? country : '';
          },
          cellClass: 'text-center',
          cellRenderer: 'FlagRenderer',
          width: 37,
          maxWidth: 37
        },
        quantity: {
          valueFormatter: this.typeValueFormatter.bind(this, 'float.fractionalQuantity'),
          cellRenderer: this.cellRendererARIA
        },
        trade: {
          cellClassRules: {
            'text-center': () => true,
            'trading-button-cell': () => true,
            'actionable-cell': params => (params.data ? params.data.canBuy || params.data.canSell : false)
          },
          cellRenderer: 'TdTradingButtonsRenderer',
          cellRendererParams: params => this.getTradingParams(params),
          width: 120,
          sortable: false,
          suppressKeyboardEvent: params => {
            const key = params.event.code || params.event.key;
            return key === 'Tab';
          },
          type: ['actionable']
        },
        bookValue: {
          valueFormatter: params => this.$f(params.value, 'float.priceTD'),
          aggFunc: 'sum',
          cellRenderer: this.cellRendererARIA
        },
        marketValue: {
          valueFormatter: params =>
            this.twoDecimalPlaces ? this.$f(params.value, 'float.price') : this.$f(params.value, 'float.priceTD'),
          aggFunc: 'sum',
          cellRenderer: this.cellRendererARIA
        },
        averageCostPerUnit: {
          valueFormatter: this.typeValueFormatter.bind(this, 'float.priceTD'),
          cellRenderer: this.cellRendererARIA
        },
        loanValue: {
          valueFormatter: this.typeValueFormatter.bind(this, 'float.priceTD'),
          cellRenderer: this.cellRendererARIA
        },
        dailyPL: {
          cellClassRules: ChangeRulesPack,
          valueFormatter: params => {
            if (params.value || parseFloat(params.value) === 0.0) {
              if (Number(params.value) === 0.0) return '0.00';
              return this.twoDecimalPlaces
                ? this.$f(params.value, 'float.price.change')
                : this.$f(params.value, 'float.priceTD.change');
            } else {
              return '-';
            }
          },
          aggFunc: 'sum'
        },
        dailyPLPercent: {
          cellClassRules: ChangeRulesPack,
          aggFunc: ({ values }) => {
            let totalDailyPL = this.sumReducer(values, 'dailyPL'),
              totalMarketValue = this.sumReducer(values, 'marketValue');
            return {
              dailyPL: totalDailyPL,
              marketValue: totalMarketValue,
              toString: () => {
                const value = this.calculateDailyPercent(totalDailyPL, totalMarketValue);
                if (Number.isNaN(value)) return '-';
                else return this.$f(value, 'float.percentage.change');
              }
            };
          },
          valueFormatter: params => this.$f(params.value, 'float.percentage.change'),
          valueGetter: params => {
            if (!params.node.group) {
              return {
                dailyPL: params.data.dailyPL,
                marketValue: params.data.marketValue,
                toString: () => {
                  const value = this.calculateDailyPercent(params.data.dailyPL, params.data.marketValue);
                  if (Number.isNaN(value)) return 'NaN';
                  else return value.toString();
                }
              };
            }
          }
        },
        unrealizedPL: {
          cellClassRules: ChangeRulesPack,
          valueFormatter: params =>
            this.twoDecimalPlaces
              ? this.$f(params.value, 'float.price.change')
              : this.$f(params.value, 'float.priceTD.change'),
          aggFunc: 'sum'
        },
        unrealizedPLPercent: {
          cellClassRules: ChangeRulesPack,
          aggFunc: ({ values }) => {
            let totalUnrealizedPL = this.sumReducer(values, 'unrealizedPL'),
              totalBookValue = this.sumReducer(values, 'bookValue');
            return {
              unrealizedPL: totalUnrealizedPL,
              bookValue: totalBookValue,
              toString: () => {
                const value = this.calculateUnrealizedPLPercent(totalUnrealizedPL, totalBookValue);
                if (Number.isNaN(value)) return 'NaN';
                else return value.toString();
              }
            };
          },
          valueGetter: params => {
            if (!params.node.group) {
              return {
                unrealizedPL: params.data.unrealizedPL,
                bookValue: params.data.bookValue,
                toString: () => {
                  const value = this.calculateUnrealizedPLPercent(params.data.unrealizedPL, params.data.bookValue);
                  if (Number.isNaN(value)) return 'NaN';
                  else return value.toString();
                }
              };
            }
          },
          valueFormatter: params => this.$f(params.value, 'float.percentage.change')
        },
        weight: {
          valueFormatter: params => this.$f(params.value, 'float.percentage'),
          aggFunc: 'sum',
          cellRenderer: this.cellRendererARIA
        }
      };
    },
    groupColumns() {
      return [
        {
          field: 'sector',
          valueGetter: params => {
            return params.data ? params.data.sector : '';
          },
          rowGroup: this.groupSector,
          rowGroupIndex: 1,
          hide: true
        },
        {
          field: 'underlying',
          valueGetter: params => {
            const underlyingTicker = params.data?.underlyingTicker?.trim() || this.displayTicker(params.data);
            const country = params.data.symbol ? params.data.symbol.market : 'CA';
            return `${underlyingTicker}_${country}`;
          },
          valueFormatter: params => {
            return params.value ? params.value.split('_')[0] : '';
          },
          rowGroup: this.groupUnderlying,
          rowGroupIndex: 2,
          hide: true,
          cellRenderer: this.cellRendererARIA
        }
      ];
    },
    isBuyingPower() {
      const accountType =
        this.selectedAccount && this.selectedAccount.type ? this.selectedAccount.type.stringValue : '';
      return ['E', 'F', 'G', 'I', 'J', 'K', 'H', 'L', 'M', 'N', 'S', 'T', 'U', 'V'].includes(accountType);
    },
    tickerColDefForGroupedCol() {
      return {
        valueGetter: params => {
          let priceChange = params.data['q._NETCHNG_1'];
          let change = '';
          if (priceChange > 0) change = 'positive';
          else if (priceChange < 0) change = 'negative';
          return {
            value: this.displayTicker(params.data),
            change,
            toString: function() {
              return this.value;
            }
          };
        },
        equals: (val1, val2) => {
          let tickerEqual = val1.value === val2.value;
          let changeEqual = val1.change === val2.change;
          return tickerEqual && changeEqual;
        },
        checkboxSelection: () => this.showCustomSelection,
        width: 160,
        cellClass: this.getTickerClass,
        comparator: (a, b) => a.localeCompare(b),
        cellStyle: params => {
          if (params.data && params.data['q.TRD_STATUS'] && this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])) {
            return { 'border-style': 'solid', 'border-width': '1px', 'border-color': 'var(--red)' };
          } else return { border: 'none' };
        },
        tooltipValueGetter: params => {
          if (params.data) {
            let toolTipValue = params.data['q._DSPLY_NAME'];
            if (this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])) {
              toolTipValue = `${toolTipValue} ( ${this.i18n(
                params.data['q.TRD_STATUS'].trim(),
                'white-label.globalLabels.tradingHalt'
              )} )`;
            }
            return toolTipValue;
          } else return '';
        }
      };
    },
    tickerColDef() {
      return {
        valueGetter: params => {
          let priceChange = params.data['q._NETCHNG_1'];
          let change = '';
          if (priceChange > 0) change = 'positive';
          else if (priceChange < 0) change = 'negative';
          return {
            value: this.displayTicker(params.data),
            change,
            toString: function() {
              return this.value;
            }
          };
        },
        equals: (val1, val2) => {
          let tickerEqual = val1.value === val2.value;
          let changeEqual = val1.change === val2.change;
          return tickerEqual && changeEqual;
        },
        cellRenderer: 'GenericTickerRenderer',
        cellRendererParams: params => {
          if (params.data && params.data['q.TRD_STATUS']) {
            return { ticker: this.displayTicker(params.data), tradeHaltValue: params.data['q.TRD_STATUS'] };
          } else return { ticker: this.displayTicker(params.data) };
        },
        cellStyle: params => {
          if (params.data && params.data['q.TRD_STATUS'] && this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])) {
            return { 'border-style': 'solid', 'border-width': '1px', 'border-color': 'var(--red)' };
          } else return { border: 'none' };
        },
        tooltipValueGetter: params => {
          if (params.data) {
            let toolTipValue = params.data['q._DSPLY_NAME'];
            if (this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])) {
              toolTipValue = `${toolTipValue} ( ${this.i18n(
                params.data['q.TRD_STATUS'].trim(),
                'white-label.globalLabels.tradingHalt'
              )} )`;
            }
            return toolTipValue;
          } else return '';
        },
        checkboxSelection: () => this.showCustomSelection,
        width: 160,
        cellClass: this.getTickerClass,
        comparator: (a, b) => a.value.localeCompare(b.value)
      };
    },
    showCustomSelection: {
      get: function() {
        return this.$store.state.widget[this.wid].showCustomSelection;
      },
      set: function(val) {
        this.$store.commit(`widget/${this.wid}/SET_CUSTOM_SELECTION`, val);
        this.clearRowSelection();
        let colIds = ['ticker'];
        this.autoSizeColumns(colIds);
      }
    },
    symbolSearch() {
      return this.widgetStore.symbolSearch || '';
    },
    currencyConversionLabel() {
      if (this.currency === 'CAD') {
        return `1 CAD = ${
          Number.parseFloat(this.currenciesBidAsk.CAD.bid) > 0
            ? this.$f(1 / this.currenciesBidAsk.CAD.bid, 'float')
            : '0'
        } USD`;
      } else {
        return `1 USD = ${this.$f(this.currenciesBidAsk.CAD.bid, 'float')} CAD`;
      }
    },
    loggedIn() {
      return this.$store.getters['auth/loggedIn'];
    },
    lastUpdatedTimeStamp() {
      return this.$store.state.widget[this.wid].lastUpdatedTimeStamp;
    }
  },
  watch: {
    'widgetStore.groupSector': {
      handler(val) {
        this.groupSector = val;
        if (this.gridApi) {
          this.handleSectorUnderlyingChange();
        }
      },
      immediate: true
    },
    'widgetStore.groupUnderlying': {
      handler(val) {
        this.groupUnderlying = val;
        if (this.gridApi) {
          this.handleSectorUnderlyingChange();
        }
      },
      immediate: true
    },
    tableFids: {
      handler(newFids, prevFids) {
        this.fetchRows(newFids, prevFids);
      },
      immediate: false
    },
    symbolSearch: {
      //update in store trigger the filter
      immediate: true,
      handler() {
        if (this.gridOptions.api) {
          this.gridOptions.api.onFilterChanged();
        }
      }
    },
    loggedIn: {
      //This method should be replaced once we finalize on how do
      // we persist the state values after logout. For every new logins,
      // default account from config data should be the first one to be
      // displayed in account drop down. And we do not save the account id
      // in our backend due to seucirty reasons. Hence, clearing out this
      // value during logout.
      immediate: true,
      handler() {
        if (!this.loggedIn) {
          this.selectedAccountId = '';
        }
      }
    }
  },
  methods: {
    displayTicker(position) {
      if (position) {
        if (this.isValidPosition(position)) {
          return position.symbol.tickerSymbol;
        } else {
          return position.name;
        }
      } else return '';
    },
    getTickerClass(params) {
      if (!params.data) return '';
      let classes = ['text-left'];

      const val = params.data['q._NETCHNG_1'];
      if (val < 0) return classes.concat('ticker-negative');
      if (val > 0) return classes.concat('ticker-positive');
      return classes.concat('text-primary');
    },
    handleSectorUnderlyingChange() {
      this.changeGroupingDisplay();
      this.autosizeTickerColumn();
    },
    changeGroupingDisplay() {
      if (this.groupSector && this.groupUnderlying) {
        this.gridApi.setGroupRemoveLowestSingleChildren(true);
        this.gridApi.setGroupRemoveSingleChildren(false);
      } else if (this.groupSector) {
        this.gridApi.setGroupRemoveSingleChildren(false);
        this.gridApi.setGroupRemoveLowestSingleChildren(false);
      } else if (this.groupUnderlying) {
        this.gridApi.setGroupRemoveSingleChildren(true);
        this.gridApi.setGroupRemoveLowestSingleChildren(true);
      }
    },
    autosizeTickerColumn() {
      const tickerCol = this.gridOptions.columnApi.getAllColumns().find(colDef => {
        return colDef.colId.startsWith('ticker');
      });
      const groupCol = this.gridOptions.columnApi.getColumn('ag-Grid-AutoColumn');
      if (!this.groupSector && !this.groupUnderlying) {
        if (tickerCol) {
          if (groupCol) this.gridOptions.columnApi.setColumnWidth(tickerCol, groupCol.getActualWidth());
          else this.gridOptions.columnApi.setColumnWidth(tickerCol, 120);
        }
      } else {
        if (groupCol) {
          if (tickerCol) this.gridOptions.columnApi.setColumnWidth(groupCol, tickerCol.getActualWidth());
          else this.gridOptions.columnApi.setColumnWidth(groupCol, 120);
        }
      }
    },
    typeValueFormatter(format = 'float.price', params) {
      if (params.node.group) {
        if (!this._.isNumber(params.value)) {
          return '';
        }
      }
      return this.$f(params.value, format);
    },
    onRowSelected() {
      let selectedNodes = this.gridApi.getSelectedNodes();
      const selectedIds = selectedNodes.reduce((acc, node) => [...acc, node.id], []);
      this.$store.commit(`widget/${this.wid}/SET_SELECTED_ROWS`, selectedIds);
      this.updateAccountSummary();
    },
    i18n(subkey, ns) {
      if (ns === 'white-label.globalLabels.fids') subkey = subkey.replace('.', '|');
      return this.$t(`${ns || this.i18nNamespace}.${subkey}`);
    },
    loadAccountDetails() {
      let arrayOfFloatingWidgets = this.$store.state.floatingWorkspace.items.map(item => item.id);

      let activeWorkspaceItems = this.$store.state.workspaces[this.$store.state.workspaceManager.active].items;
      let arrayOfAllWidgetsInActiveWorkspace = Object.keys(activeWorkspaceItems)
        .map(key => activeWorkspaceItems[key])
        .map(item => item.content[0].id);

      let activeOrderEntryWidgets = arrayOfAllWidgetsInActiveWorkspace
        .concat(arrayOfFloatingWidgets)
        .filter(item => item.includes('orderEntry'));

      this.cash = this.buyingPower = 0;
      this.$store.dispatch('accounts/fetchAccountDetailsFromTD', this.selectedAccountId).then(response => {
        this.cash = response.cash || 0;
        this.buyingPower = response.buyingPower || 0;

        activeOrderEntryWidgets.forEach(widgetId => {
          let accountIDOE = this.$store.state.widget[widgetId].accountId;
          if (accountIDOE === this.selectedAccountId) {
            this.$store.commit(`widget/${widgetId}/SET_CASH`, response.cash || '-');
            this.$store.commit(`widget/${widgetId}/SET_BUYING_POWER`, response.buyingPower || '-');
          }
        });
      });
    },
    onAccountChange(account) {
      this.selectedAccount = account;
      this.isOrderEntryAccount = this.orderEntryAccounts.filter(oeAccount => oeAccount.id === account.id).length === 1;
      if (this.selectedAccountId !== account.id) {
        this.selectedAccountId = account.id;
        this.currency = account.currency;
      }
      this.loadAccountDetails();
      if (this.gridApi) this.clearRowSelection();
      this.positions = [];
      this.customSummary = this.defaultSummary();
      this.totalAccountSummary = this.defaultSummary();
      this.streamingUnsubscribe(this.quoteSubscriptionId);
      this.quoteSubscriptionId = null;
      this.fetchPositions();
    },
    onCurrencyChanged(value) {
      this.currency = value;
    },
    onSymbolChange(text) {
      this.$store.commit(`widget/${this.wid}/UPDATE_SYMBOLSEARCH`, text);
    },
    groupBySector($event) {
      if (this.gridOptions) this.gridOptions.maintainColumnOrder = true;
      this.$store.commit(`widget/${this.wid}/UPDATE_GROUPSECTOR`, $event);
    },
    groupByUnderlying($event) {
      if (this.gridOptions) this.gridOptions.maintainColumnOrder = true;
      this.$store.commit(`widget/${this.wid}/UPDATE_GROUPUNDERLYING`, $event);
    },
    parseFloatForTradingData(num, decimals) {
      return this._.isNumber(num) ? Number.parseFloat(num).toFixed(decimals) : Number(0.0);
    },
    onGridReady(ev) {
      this.gridApi = ev.api;
      this.onTableReady(ev, this.i18n('widget-title'));
      this.changeGroupingDisplay();
    },
    applyRowSelection() {
      if (this.showCustomSelection && !this._.isEmpty(this.positions)) {
        const selectedRows = this.widgetStore.selectedRows || [];
        selectedRows.forEach(id => {
          const node = this.gridApi.getRowNode(id);
          if (node) node.setSelected(true);
        });
      }
    },
    clearRowSelection() {
      const selectedRows = this.widgetStore.selectedRows || [];
      this.$store.commit(`widget/${this.wid}/SET_SELECTED_ROWS`, []);
      selectedRows.forEach(id => {
        const node = this.gridApi.getRowNode(id);
        if (node) node.setSelected(false);
      });
      this.customSummary = this.defaultSummary();
    },
    fetch() {}, //mixin dummy implementation
    fetchRows(newFids, prevFids) {
      if (newFids && prevFids) {
        const reload = newFids.some(newFid => {
          if (!newFid.startsWith('td.')) {
            return !prevFids.includes(newFid);
          }
          return false;
        });
        if (reload) {
          this.fetchPositions();
        }
      }
    },
    getTextColor(val) {
      const v = parseFloat(val);
      return v === 0 ? '' : v > 0 ? 'text-positive' : 'text-negative';
    },
    applySymbolFilterForRow(node) {
      const row = node.data;
      const ticker = (row.symbol ? row.symbol.tickerSymbol : undefined) || row.name;
      return ticker.toLowerCase().startsWith(this.symbolSearch.toLowerCase());
    },
    handleSymbolChange(text) {
      this.$store.commit(`widget/${this.wid}/UPDATE_SYMBOLSEARCH`, text);
    },
    togglePL() {
      this.plExpanded = !this.plExpanded;

      !this.plExpanded
        ? this.$nextTick(() => {
            this.$refs.buttonPlus.focus();
          })
        : this.$nextTick(() => {
            this.$refs.buttonMinus.focus();
          });
    },
    updateLink(params) {
      let node = params.node;
      if (node && node.data) {
        this.sendLinkUpdate(node.data.ric || node.data.symbol.companySymbol);
      }
    },
    updateLinkAndNodes(params) {
      // ag grid selected event gets called twice - for selected and deselected rows.
      if (params.node.selected) {
        this.updateLink(params);
      }
      this.onRowSelected();
    },
    refresh() {
      this.fetchPositions();
      this.loadAccountDetails();
      this.$store.commit(`widget/${this.wid}/UPDATE_LAST_UPDATED_TIME_STAMP`, moment().format('hh:mm:ss A'));
    },
    getTradingParams(params) {
      if (params.node.group) return;
      const position = params.data;
      const symbol = position.symbol;
      if (!symbol) return {};
      const isOption = symbol.type === 'OPTION';
      const isFractional = !Number.isInteger(position.quantity);
      const props = {};
      if (isOption) {
        props['underlyingSymbol'] = position.underlyingRic;
      }
      props['accountId'] = this.selectedAccountId;
      props['symbol'] = position.ric;
      props['isOption'] = isOption;
      props['quantity'] = position.quantity;
      props['isFractional'] = isFractional;
      props['isOrderEntryAccount'] = this.isOrderEntryAccount;
      return props;
    },
    invokeTabNextCell() {
      this.gridApi.tabToNextCell();
    },
    invokeTabPreviousCell() {
      this.gridApi.tabToPreviousCell();
    },
    onRowGroupOpened(params) {
      const cellElement = this.$el.querySelector(
        `div[row-index='${params.node.rowIndex}'] > div[col-id="ag-Grid-AutoColumn"]`
      );
      let actionElement = cellElement.querySelector('span.ag-group-expanded');
      if (params.expanded) actionElement.classList.add('active');
      else actionElement.classList.remove('active');
      actionElement = cellElement.querySelector('span.ag-group-contracted');
      if (!params.expanded) actionElement.classList.add('active');
      else actionElement.classList.remove('active');
    },
    onCellFocusedWidget(params) {
      const column = params.column;
      if (!column) return;

      const node = column.gridApi.getDisplayedRowAtIndex(params.rowIndex);
      if (node.group && column.colId === 'ag-Grid-AutoColumn') {
        // for groups get expand/collapse span
        const cellElement = this.$el.querySelector(
          `div[row-index="${node.rowIndex}"] > .ag-cell[col-id="ag-Grid-AutoColumn"]`
        );

        if (cellElement) {
          let actionElement = cellElement.querySelector('div.ag-selection-checkbox:not(.ag-hidden) input');

          if (actionElement) actionElement.focus();
          else {
            actionElement = cellElement.querySelector(
              'span.ag-group-expanded:not(.ag-hidden),span.ag-group-contracted:not(.ag-hidden)'
            );
            if (actionElement) actionElement.classList.add('active');
          }

          this.isTabPressed = false;
          this.isShiftTabPressed = false;
          return;
        }
      } else return this.onCellFocused(params);
    }
  },
  beforeDestroy() {
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>
<style lang="scss">
@import '~@/styles/vendor/ag-grid';
@import '~@/styles/widgets/holdings-widget';
</style>
